import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGSAP } from '@gsap/react';
import SEO from './SEO';

const Heritage: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const navigate = useNavigate();

    useGSAP(() => {
        // Animate Main Title
        if (titleRef.current) {
            gsap.fromTo(titleRef.current,
                { y: 50, autoAlpha: 0 },
                { y: 0, autoAlpha: 1, duration: 1.2, ease: 'power3.out' }
            );
        }

        // Animate Text Blocks
        gsap.utils.toArray<HTMLElement>('.text-reveal').forEach((element) => {
            gsap.fromTo(element,
                { y: 30, autoAlpha: 0 },
                {
                    scrollTrigger: {
                        trigger: element,
                        start: 'top 90%',
                        toggleActions: 'play none none reverse',
                    },
                    y: 0,
                    autoAlpha: 1,
                    duration: 1,
                    ease: 'power2.out',
                }
            );
        });

        // Animate Images
        gsap.utils.toArray<HTMLElement>('.img-reveal').forEach((element) => {
            gsap.fromTo(element,
                { y: 50, scale: 1.1, autoAlpha: 0 },
                {
                    scrollTrigger: {
                        trigger: element,
                        start: 'top 85%',
                        scrub: 1,
                    },
                    y: 0,
                    scale: 1,
                    autoAlpha: 1,
                    duration: 1,
                }
            );
        });

    }, { scope: containerRef });

    return (
        <div ref={containerRef} className="pt-32 pb-24 px-6 bg-diwan-900 text-diwan-cream min-h-screen">
            <SEO
                title="Heritage & Legacy"
                description="Founded in 1958, D'mans Furniture carries a 65-year legacy of excellence in handcrafted luxury furniture and interior design."
                url="/about"
            />

            {/* Page Header */}
            <div className="max-w-4xl mx-auto text-center mb-24">
                <p className="text-diwan-gold uppercase tracking-[0.4em] text-xs font-sans mb-6">Established 1958</p>
                <h1 ref={titleRef} className="text-5xl md:text-7xl font-serif leading-none">
                    Ranbir Singh <br /> <span className="italic text-diwan-gold">Dhiman & Sons</span>
                </h1>
            </div>

            {/* Intro Section with Letter Content */}
            <section className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 mb-32 items-start">
                <div className="text-reveal space-y-8 font-serif text-xl md:text-2xl leading-relaxed text-diwan-cream/90">
                    <p>
                        "We feel proud & pleased to mention that there are not many companies in India which can claim to have so much experience in Furniture Manufacturing, Interior Designing, Restoration & Reproduction."
                    </p>
                    <p className="text-base font-sans text-diwan-cream/60 tracking-wide uppercase">
                        — A Legacy of Excellence
                    </p>
                </div>
                <div className="text-reveal space-y-6 font-sans text-diwan-cream/70 leading-relaxed">
                    <p>
                        Ours is a professional and well-established organization engaged in Turnkey Interior Projects, Exports & Imports. All our products and works are manufactured in-house. We have been in this business for over fifty years now.
                    </p>
                    <p>
                        With a shrinking skilled workforce in almost all highly skill-based industries, our company is one of the very few which has survived the onslaught. Founded in 1958, we have a wealth of experience gained over the past five decades.
                    </p>
                    <p>
                        We operate one of the most advanced solid wood furniture factories in the world today, leveraging our years of experience to produce high-quality wooden products.
                    </p>
                </div>
            </section>

            {/* Image Breaker */}
            <section className="w-full h-[60vh] md:h-[80vh] relative mb-32 overflow-hidden">
                <div className="img-reveal absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1511166697172-6c1740d2d5fb?q=80&w=2574&auto=format&fit=crop')" }}></div>
                <div className="absolute inset-0 bg-black/30"></div>
                <div className="absolute bottom-10 right-10 text-right">
                    <h3 className="text-4xl font-serif italic text-white">The Art of Wood</h3>
                </div>
            </section>

            {/* Certifications & Memberships */}
            <section className="max-w-5xl mx-auto mb-32 border-t border-diwan-800 pt-20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <div className="text-reveal">
                        <h3 className="text-3xl font-serif text-diwan-cream mb-4">Official Memberships</h3>
                        <p className="text-diwan-cream/60 font-sans leading-relaxed">
                            Our company is also an active corporate member of the Confederation of Indian Industry (CII) and Federation of Indian Chambers of Commerce & Industry (FICCI). Currently serving as Executive Council Member of the Chandigarh State Council of CII.
                        </p>
                    </div>
                    <div className="space-y-8 text-reveal">
                        <div className="border-l-2 border-diwan-gold pl-6">
                            <h4 className="text-xl font-serif text-diwan-gold">Skill India Initiative</h4>
                            <p className="text-diwan-cream/70 text-sm mt-2">
                                "Founder Member" of the Furniture & Fittings Sector Skill Council. Serving as "Governing Council Member" mandated by NSDC under the Ministry of Skill Development & Entrepreneurship.
                            </p>
                        </div>
                        <div className="flex gap-6 opacity-60">
                            <div className="h-12 border border-diwan-cream/20 px-4 flex items-center justify-center text-xs uppercase tracking-widest text-diwan-cream/50">CII</div>
                            <div className="h-12 border border-diwan-cream/20 px-4 flex items-center justify-center text-xs uppercase tracking-widest text-diwan-cream/50">FICCI</div>
                            <div className="h-12 border border-diwan-cream/20 px-4 flex items-center justify-center text-xs uppercase tracking-widest text-diwan-cream/50">NSDC</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* The D'mans Brand Section */}
            <section className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-32">
                <div className="order-2 md:order-1 img-reveal h-[500px] w-full relative">
                    <img
                        src="https://images.unsplash.com/photo-1533090368676-1fd25485db88?q=80&w=2669&auto=format&fit=crop"
                        alt="Detailing"
                        className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                    />
                    <div className="absolute -bottom-6 -left-6 bg-diwan-gold p-6 hidden md:block">
                        <span className="text-diwan-900 font-bold font-serif text-2xl">D'MANS</span>
                    </div>
                </div>

                <div className="order-1 md:order-2 text-reveal">
                    <span className="text-diwan-gold uppercase tracking-[0.3em] text-xs font-sans">Brand Identity</span>
                    <h2 className="text-4xl font-serif text-diwan-cream mt-4 mb-8">The D'mans Promise</h2>
                    <div className="space-y-6 text-diwan-cream/70 font-sans leading-relaxed">
                        <p>
                            We are currently marketing our products under the "D'mans" brand name. At D'mans, words such as quality in service, delivery, styling, and superb products are not merely slogans -- they are part of an everyday approach to bring the best contemporary classics available.
                        </p>
                        <p>
                            Unlike the mass of cheaper foil veneered options in the market today, our Furniture is made from solid teak wood and carefully selected from renewable sources -- so you can expect generations of service and pleasure from our products.
                        </p>
                        <p className="font-serif text-xl text-diwan-cream italic">
                            "Our aim is to create pieces which are as practical and enduring as they are beautiful."
                        </p>
                    </div>

                    {/* CTA to Products */}
                    <div className="pt-8">
                        <button
                            onClick={() => navigate('/products')}
                            className="group flex items-center gap-3 px-6 py-3 border border-diwan-cream/20 hover:border-diwan-gold hover:bg-diwan-gold transition-all duration-300"
                        >
                            <span className="text-xs uppercase tracking-widest font-bold group-hover:text-white">View Our Collection</span>
                            <ArrowRight size={14} className="group-hover:text-white" />
                        </button>
                    </div>
                </div>
            </section>

            {/* Signature Footer */}
            <div className="max-w-2xl mx-auto text-center border p-12 border-diwan-gold/20 bg-diwan-800/30 text-reveal">
                <p className="font-serif text-lg text-diwan-cream/80 mb-6 italic">
                    "To experience the real quality, choice and service D'mans offers, a visit to our Show Centre is highly recommended."
                </p>
                <div className="flex flex-col items-center justify-center gap-2">
                    <span className="font-handwriting font-serif text-3xl text-diwan-gold">Sukhjit S. Dhiman</span>
                    <span className="text-xs uppercase tracking-widest text-diwan-cream/50">Chief Executive Officer</span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-diwan-cream/30 mt-4">11, Timber Market, Chandigarh</span>
                </div>
            </div>

        </div>
    );
};

export default Heritage;